/**
 * stuburt-card-swatches.js — in-place colour swap for collection-card swatches.
 *
 * Pressing a swatch on a product card no longer navigates: it swaps the card's
 * cover image to that colour's stock packshot, re-points the card's links at the
 * colour's variant URL (so clicking the card opens the PDP on that colour) and
 * re-scopes the quick-add popup's preselected colour. Pressing the original
 * colour's swatch restores the card exactly as served.
 *
 * Markup + per-swatch data (packshot src/srcset, variant URL, colour value):
 * snippets/stuburt-card-swatches.liquid.
 *
 * Fallback by design: anywhere the row is NOT inside a <product-card> with a
 * card-gallery slideshow (homepage SS26 tiles, collab grids), or a colour has
 * no packshot data, the anchors keep their native ?variant= navigation.
 *
 * Non-negotiables: Custom Element (no framework), full teardown in
 * disconnectedCallback (the editor re-renders aggressively). No animation is
 * added (the swap is instant), so no reduced-motion companion is needed.
 */

class StuburtCardSwatches extends HTMLElement {
  #onClick = null;
  /** Card state as served, captured before the first swap so the original
      colour's swatch can restore it. */
  #orig = null;
  /** Colour slug the card was served with (its `is-active` swatch). */
  #origSlug = '';

  connectedCallback() {
    this.#origSlug = this.querySelector('.stuburt-card-swatches__item.is-active')?.dataset.colour || '';
    this.#onClick = (e) => this.#handleClick(e);
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    if (this.#onClick) this.removeEventListener('click', this.#onClick);
    this.#onClick = null;
    this.#orig = null;
  }

  /** @param {MouseEvent} e */
  #handleClick(e) {
    const item = e.target instanceof Element && e.target.closest('.stuburt-card-swatches__item');
    if (!item || !this.contains(item)) return;

    // Only cards with a Horizon card-gallery support the in-place swap; other
    // hosts (homepage tiles, collab grids) keep the anchor's native navigation.
    const card = this.closest('product-card');
    if (!card) return;

    const slides = card.querySelectorAll('.card-gallery slideshow-slide');
    const cover = Array.prototype.find.call(slides, (s) => !s.hasAttribute('hidden'));
    const img = cover && cover.querySelector('img');
    if (!img) return;

    const toOriginal = item.dataset.colour === this.#origSlug;
    if (!toOriginal && !item.dataset.cardSrc) return; // no packshot to show — navigate instead

    e.preventDefault();
    if (item.classList.contains('is-active')) return; // already showing this colour

    if (!this.#orig) {
      this.#orig = {
        src: img.getAttribute('src'),
        srcset: img.getAttribute('srcset'),
        alt: img.getAttribute('alt'),
        hidden: Array.prototype.map.call(slides, (s) => s.hasAttribute('hidden')),
        href: card.querySelector('a[ref="productCardLink"]')?.getAttribute('href') || '',
        featuredMediaUrl: card.getAttribute('data-featured-media-url'),
        baseFilter: card.querySelector('stuburt-quick-pick')?.dataset.baseFilter,
      };
    }

    if (toOriginal) {
      if (this.#orig.src) img.setAttribute('src', this.#orig.src);
      if (this.#orig.srcset == null) img.removeAttribute('srcset');
      else img.setAttribute('srcset', this.#orig.srcset);
      if (this.#orig.alt != null) img.setAttribute('alt', this.#orig.alt);
      slides.forEach((s, i) => s.toggleAttribute('hidden', this.#orig.hidden[i]));
    } else {
      img.setAttribute('src', item.dataset.cardSrc);
      if (item.dataset.cardSrcset) img.setAttribute('srcset', item.dataset.cardSrcset);
      else img.removeAttribute('srcset');
      img.setAttribute('alt', item.getAttribute('title') || img.getAttribute('alt') || '');
      // Park the gallery on the swapped cover: the other slides belong to the
      // served colour, so hover-preview / arrows would flash the wrong colour.
      slides.forEach((s) => s.toggleAttribute('hidden', s !== cover));
    }

    // Snap the slideshow back onto the cover slide (it may sit on a hover frame).
    const slideshow = card.querySelector('.card-gallery slideshow-component');
    const coverId = cover.getAttribute('slide-id');
    if (slideshow && typeof slideshow.select === 'function' && coverId) {
      slideshow.select({ id: coverId }, undefined, { animate: false });
    }

    // Card links follow the colour, so any card click opens the PDP on it.
    const href = toOriginal ? this.#orig.href : item.getAttribute('href');
    if (href) {
      card
        .querySelectorAll('a[ref="productCardLink"], a[ref="cardGalleryLink"], a[ref="productTitleLink"]')
        .forEach((a) => a.setAttribute('href', href));
    }

    // Keep the PDP view-transition image on the shown colour.
    const transitionSrc = toOriginal ? this.#orig.featuredMediaUrl : item.dataset.cardSrc;
    if (card.hasAttribute('data-featured-media-url') && transitionSrc) {
      card.setAttribute('data-featured-media-url', transitionSrc);
    }

    // Quick-add popup opens preselected on the shown colour.
    const quickPick = card.querySelector('stuburt-quick-pick');
    const filterValue = toOriginal ? this.#orig.baseFilter : item.dataset.colourValue;
    if (quickPick && filterValue != null) quickPick.dataset.baseFilter = filterValue;

    this.querySelectorAll('.stuburt-card-swatches__item').forEach((a) => {
      const active = a === item;
      a.classList.toggle('is-active', active);
      if (active) a.setAttribute('aria-current', 'true');
      else a.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('stuburt-card-swatches')) {
  customElements.define('stuburt-card-swatches', StuburtCardSwatches);
}
